import { useCallback, useEffect, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { TEAM_GROUPS } from '../../../../../shared/featureFlags/flagNames';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { useQuery } from '../../../../contexts/RouterContext';
import { isFeatureEnabled } from '../../../../helpers/features/featureFlags';
import { useInputValue } from '../../../../hooks';
import useHasPermission from '../../../../shared/permissions/useHasPermission';
import { ErrorAlert } from '../../../generic';
import AccessGate from '../../../generic/Gates/AccessGate';
import Tabs from '../../../generic/Tabs';
import Header from '../../../shared/settings/SettingsPageHeader';
import SettingsPageTableActions from '../../../shared/settings/SettingsPageTableActions';
import { teamFeature } from '../../billing/features/consts';

import { CreateTeamModal } from './CreateEditTeamModal';
import CreateTeamGroupModal from './teamGroups/CreateTeamGroupModal';
import TeamGroupsTable from './teamGroups/TeamGroupsTable';
import TeamsTable from './teamsTable';

type Props = {
  fetchTeamsError: Error | null;
  isTeamGroupsEnabled: boolean;
  activeTab?: 'teams' | 'teamGroups';
  setActiveTab: (tab: 'teams' | 'teamGroups') => void;
};

const OrganizationTeamsIndex: VoidFunctionComponent<Props> = ({
  fetchTeamsError,
  isTeamGroupsEnabled,
  activeTab,
  setActiveTab,
}) => {
  const hasPermission = useHasPermission();
  const [searchValue, setSearchValue] = useInputValue();
  const [isCreateTeamModalOpen, setIsCreateTeamModalOpen] = useState(false);
  const [isCreateTeamGroupModalOpen, setIsCreateTeamGroupModalOpen] = useState(false);

  const onShowCreateTeamModal = useCallback(() => setIsCreateTeamModalOpen(true), []);
  const onHideCreateTeamModal = useCallback(() => setIsCreateTeamModalOpen(false), []);

  const onShowCreateTeamGroupModal = useCallback(() => setIsCreateTeamGroupModalOpen(true), []);
  const onHideCreateTeamGroupModal = useCallback(() => setIsCreateTeamGroupModalOpen(false), []);

  // This check also applies to TeamGroups, since they are ultimately just a collection of teams.
  const canCreateTeams = hasPermission({ resourceType: ResourceType.Team, action: Action.Create });

  return (
    <AccessGate level="premium" behavior="showPage" focusFeature={teamFeature}>
      {fetchTeamsError && (
      // Show the error alert on the page itself only when there isn't an open modal.
        <ErrorAlert status={{ errors: [fetchTeamsError] }} />
      )}
      <Header heading="Teams">
        <Header.Paragraph>
          Create teams and assign users and projects to them.
          {' '}
          Users without org-level permissions can only access data associated with their teams.
        </Header.Paragraph>
      </Header>
      {isTeamGroupsEnabled && (
        <Tabs>
          <Tabs.Tab
            label="Teams"
            //  This handles the case where the tab is not set.
            isActive={!activeTab || activeTab === 'teams'}
            onSelect={() => setActiveTab('teams')}
          />
          <Tabs.Tab
            label="Team Groups"
            isActive={activeTab === 'teamGroups'}
            onSelect={() => setActiveTab('teamGroups')}
          />
        </Tabs>
      )}
      <SettingsPageTableActions>
        <SettingsPageTableActions.TooltipButton
          id="add-team-button"
          size="large"
          type="secondary"
          onClick={activeTab === 'teams' ? onShowCreateTeamModal : onShowCreateTeamGroupModal}
          isDisabled={!canCreateTeams}
          showTooltip={!canCreateTeams}
          tooltip={`You are not permitted to create ${activeTab === 'teams' ? 'teams' : 'team groups'}.`}
        >
          Create {activeTab === 'teams' ? 'Team' : 'Team Group'}
        </SettingsPageTableActions.TooltipButton>
        <SettingsPageTableActions.SearchInput
          label={`Search ${activeTab === 'teams' ? 'teams' : 'team groups'}`}
          value={searchValue}
          onChange={setSearchValue}
          placeholder={`Search ${activeTab === 'teams' ? 'teams' : 'team groups'}`}
          size="medium"
        />
      </SettingsPageTableActions>
      {activeTab === 'teams' ? (
        <TeamsTable
          search={searchValue}
        />
      ) : (
        <TeamGroupsTable search={searchValue} />
      )}
      {canCreateTeams && (
        <CreateTeamModal
          isOpen={isCreateTeamModalOpen}
          onHide={onHideCreateTeamModal}
        />
      )}
      {isTeamGroupsEnabled && (
        <CreateTeamGroupModal
          isOpen={isCreateTeamGroupModalOpen}
          onHide={onHideCreateTeamGroupModal}
        />
      )}
    </AccessGate>
  );
};

/**
 * This is the teams settings page, which displays a list of teams
 * and allows the user to go to the edit page or to delete the team.
 *
 * Container component which will "inject" hook-specific props into the component.
 */
const TeamsIndexContainer = (props: Omit<Props, 'isTeamGroupsEnabled'>) => {
  const isTeamGroupsEnabled = isFeatureEnabled(TEAM_GROUPS);
  const [query, setQuery] = useQuery<{ tab?: 'teams' | 'teamGroups' }>();

  const setActiveTab = useCallback((tab: 'teams' | 'teamGroups') => {
    //  Always unset the query if team groups are not enabled.
    if (!isTeamGroupsEnabled) {
      setQuery({ tab: undefined });
      return;
    }

    if (!tab || (tab !== 'teams' && tab !== 'teamGroups')) {
      setQuery({ tab: 'teams' });
    } else {
      setQuery({ tab });
    }
  }, [isTeamGroupsEnabled, setQuery]);

  //  Ensure that there is always a tab set.
  useEffect(() => {
    if (!isTeamGroupsEnabled || !query?.tab) {
      setActiveTab('teams');
    }
  }, [isTeamGroupsEnabled, query?.tab, setActiveTab]);

  return (
    <OrganizationTeamsIndex
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      isTeamGroupsEnabled={isTeamGroupsEnabled}
      activeTab={query.tab || 'teams'}
      setActiveTab={setActiveTab}
    />
  );
};

export default TeamsIndexContainer;
